import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search } from 'lucide-react';
import { DataTable, type Column } from '@/components/common/DataTable';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { productionService } from '@/services/production';
import { inventoryService } from '@/services/inventory';
import { pickerLabel } from '@/lib/inventory-label';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import type { ProductionRecipeRow, ProductionRecipeStatus } from '@/types/production';
import { RecipeEditorSheet } from './RecipeEditorSheet';

type StatusFilter = 'all' | ProductionRecipeStatus;

const STATUS_LABEL: Record<ProductionRecipeStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  ARCHIVED: 'Archived',
};
const STATUS_TONE: Record<ProductionRecipeStatus, 'muted' | 'success'> = {
  DRAFT: 'muted',
  ACTIVE: 'success',
  ARCHIVED: 'muted',
};

export function ProductRecipesPage() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const query = useQuery({
    queryKey: ['production', 'recipes', { status, search: debounced }],
    queryFn: () =>
      productionService.listRecipes({
        status: status === 'all' ? undefined : status,
        search: debounced || undefined,
        pageSize: 100,
      }),
  });

  const rows = query.data?.items ?? [];

  const tabs: StatusTabOption[] = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'ACTIVE', label: 'Active' },
      { value: 'DRAFT', label: 'Draft' },
      { value: 'ARCHIVED', label: 'Archived' },
    ],
    [],
  );

  const columns: Column<ProductionRecipeRow>[] = [
    {
      key: 'targetItem',
      header: 'Finished good',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-foreground">{r.targetItem.name}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">{r.targetItem.sku}</div>
        </div>
      ),
    },
    { key: 'name', header: 'Recipe', cell: (r) => <span className="text-foreground">{r.name}</span> },
    {
      key: 'steps',
      header: 'Steps',
      cell: (r) => <span className="tabular-nums text-muted-foreground">{r._count.steps}</span>,
    },
    {
      key: 'source',
      header: 'Source',
      cell: (r) =>
        r.source === 'LEARNED' ? (
          <Badge variant="outline" className="text-[10px]">
            suggested{r.confidence != null ? ` · ${r.confidence}%` : ''}
          </Badge>
        ) : (
          <span className="text-[11px] text-muted-foreground">authored</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge variant={STATUS_TONE[r.status]} className="text-[10px]">
          {STATUS_LABEL[r.status]}
        </Badge>
      ),
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      cell: (r) => <span className="text-[11px] text-muted-foreground">{fmt.date(r.updatedAt)}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <ListPageHeader
        title="Product Recipes"
        description="How each finished good is made — the multi-step route (cutting, stitching, finishing) and the materials each step consumes. Job work orders run against these; the system also learns recipes from completed jobs."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> New recipe
          </Button>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <StatusTabs options={tabs} value={status} onChange={(v) => setStatus(v as StatusFilter)} />
        <div className="relative w-64 max-w-full">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item or recipe…"
            className="h-9 pl-8"
          />
        </div>
      </div>

      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          isLoading={query.isLoading}
          rowKey={(r) => r.id}
          onRowClick={(r) => setEditingId(r.id)}
          emptyTitle="No recipes yet"
          emptyDescription="Create a recipe to capture how a finished good is produced."
        />
      )}

      <RecipeEditorSheet
        recipeId={editingId}
        open={!!editingId}
        onOpenChange={(o) => !o && setEditingId(null)}
      />
      <CreateRecipeSheet
        open={creating}
        onOpenChange={setCreating}
        onCreated={(id) => {
          setCreating(false);
          setEditingId(id);
        }}
      />
    </div>
  );
}

/** Small create flow — pick the finished good + name, then jump into the editor. */
function CreateRecipeSheet({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [targetItemId, setTargetItemId] = useState('');
  const [name, setName] = useState('');

  const fgQuery = useQuery({
    queryKey: ['inventory', 'items', { kind: 'FINISHED_GOOD', forRecipe: true }],
    queryFn: () => inventoryService.list({ status: 'ACTIVE', kind: 'FINISHED_GOOD', pageSize: 300 }),
    enabled: open,
    staleTime: 30_000,
  });
  const finishedGoods = fgQuery.data?.items ?? [];

  const create = useMutation({
    mutationFn: () => {
      if (!targetItemId) throw new Error('Pick the finished good this recipe produces.');
      return productionService.createRecipe({
        targetItemId,
        name: name.trim() || finishedGoods.find((f) => f.id === targetItemId)?.name || 'Recipe',
      });
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['production', 'recipes'] });
      setTargetItemId('');
      setName('');
      onCreated(r.id);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not create recipe'),
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      eyebrow="New recipe"
      title="What is being made?"
      description="Pick the finished good. You’ll define its production steps next."
      submitLabel="Create & edit steps"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
      isSubmitting={create.isPending}
      size="lg"
    >
      <div className="space-y-3">
        <FormField label="Finished good" htmlFor="recipe-target" required>
          <Select value={targetItemId} onValueChange={setTargetItemId}>
            <SelectTrigger id="recipe-target" className="h-9">
              <SelectValue placeholder="Pick a sellable item" />
            </SelectTrigger>
            <SelectContent>
              {finishedGoods.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {pickerLabel(f)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Recipe name (optional — defaults to the item name)" htmlFor="recipe-name">
          <Input id="recipe-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Linen Shirt — standard make" />
        </FormField>
      </div>
    </FormSheet>
  );
}
